import { useState, useEffect, useCallback } from 'react';
import type { PosApprovalService } from '../../application/services/pos_service';
import type { PosApprovalModel } from '../../domain/models/PosApprovalModel';
import { useAppSelector } from '@/app/store/store';
import { posApprovalApiRedux } from '../../infra/adaptors/pos_approval.adaptor';

interface UsePosApprovalServiceProps {
  service: PosApprovalService;
  code: string | undefined;
}

export function usePosApprovalService({ service, code }: UsePosApprovalServiceProps) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cachedData = useAppSelector((state: any) => {
    if (!code) return null;
    
    const queryKey = `getApprovalData("${code}")`;
    
    return state[posApprovalApiRedux.reducerPath]?.queries?.[queryKey]?.data;
  }) as PosApprovalModel | null;

  const [approvalData, setApprovalData] = useState<PosApprovalModel | null>(cachedData);
  const [isLoading, setIsLoading] = useState<boolean>(!cachedData);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchApprovalData = useCallback(async () => {
    if (!code) {
      setIsLoading(false);
      setIsError(true);
      return;
    }

    setIsLoading(true);
    setIsError(false);

    try {
      const data = await service.getApprovalData(code);
      
      setApprovalData(data);
    } catch (error) {
      console.error('Failed to fetch approval data:', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [code, service]);

  useEffect(() => {
    if (!approvalData) {
      fetchApprovalData();
    }
  }, [fetchApprovalData, approvalData]);

  const handleApprovalAction = useCallback(async (actionType: string) => {
    if (!approvalData?.aplrd) return false;

    try {

      await service.submitApprovalAction(approvalData.aplrd, actionType);
      
      await fetchApprovalData();
      
      return true;
    } catch (error) {
      console.error("Failed to process action", error);
      return false;
    }
  }, [approvalData, service, fetchApprovalData]);

  return { 
    approvalData, 
    isLoading, 
    isError, 
    refetch: fetchApprovalData,
    handleApprovalAction
  };
}